import React, { useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  Button,
  Slider,
  Paper,
  TextField,
  InputAdornment,
} from 'omaha-design-system';
import { BottomSheetOrDialog } from './BottomSheetOrDialog';

interface CustomizeBlendDialogProps {
  open: boolean;
  onClose: () => void;
  stokWeight: number;
  marketWeight: number;
  onApply: (stokWeight: number, marketWeight: number) => void;
}

export const CustomizeBlendDialog: React.FC<CustomizeBlendDialogProps> = ({
  open,
  onClose,
  stokWeight,
  marketWeight,
  onApply,
}) => {
  // Local state for slider values (0-100 for display)
  const [localStokWeight, setLocalStokWeight] = useState(stokWeight * 100);
  const [localMarketWeight, setLocalMarketWeight] = useState(marketWeight * 100);

  // Sync local state when props change or dialog opens
  useEffect(() => {
    if (open) {
      setLocalStokWeight(stokWeight * 100);
      setLocalMarketWeight(marketWeight * 100);
    }
  }, [open, stokWeight, marketWeight]);

  const handleStokWeightChange = (_: Event, value: number | number[]) => {
    const newValue = value as number;
    setLocalStokWeight(newValue);
    // Auto-adjust market weight so they sum to 100
    setLocalMarketWeight(100 - newValue);
  };

  const handleMarketWeightChange = (_: Event, value: number | number[]) => {
    const newValue = value as number;
    setLocalMarketWeight(newValue);
    // Auto-adjust stok weight so they sum to 100
    setLocalStokWeight(100 - newValue);
  };

  const handleStokWeightInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string for typing
    if (value === '') {
      setLocalStokWeight(0);
      setLocalMarketWeight(100);
      return;
    }
    // Only allow digits
    if (!/^\d+$/.test(value)) return;
    // Parse and validate range
    const parsed = parseInt(value, 10);
    if (parsed >= 0 && parsed <= 100) {
      setLocalStokWeight(parsed);
      setLocalMarketWeight(100 - parsed);
    }
  };

  const handleMarketWeightInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string for typing
    if (value === '') {
      setLocalMarketWeight(0);
      setLocalStokWeight(100);
      return;
    }
    // Only allow digits
    if (!/^\d+$/.test(value)) return;
    // Parse and validate range
    const parsed = parseInt(value, 10);
    if (parsed >= 0 && parsed <= 100) {
      setLocalMarketWeight(parsed);
      setLocalStokWeight(100 - parsed);
    }
  };

  const handleApply = () => {
    // Convert back to 0-1 scale for API
    onApply(localStokWeight / 100, localMarketWeight / 100);
    onClose();
  };

  return (
    <BottomSheetOrDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      detent="auto"
    >
      <Stack spacing={2} sx={{ px: 3, pt: 3, pb: 3 }}>
        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Gilroy", "Instrument Sans", sans-serif',
            fontWeight: 900,
            fontSize: '19.2px',
            lineHeight: 1.248,
            color: '#F2F2F3',
          }}
        >
          Customize Projection Blend
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            fontFamily: '"Instrument Sans", sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.5,
            color: 'rgba(242, 242, 243, 0.7)',
          }}
        >
          Adjust the weights given to Stokastic Origination Projections and Stokastic Market Projections.
        </Typography>

        {/* Stokastic Origination Projections Slider */}
        <Paper
          sx={{
            backgroundColor: '#212223',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            p: 2,
          }}
        >
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              Stokastic Origination Projections
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '11.11px',
                lineHeight: 1.2,
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Bottom-up projections driven by proprietary player-level data and simulation-based modeling.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Slider
              value={localStokWeight}
              onChange={handleStokWeightChange}
              min={0}
              max={100}
              sx={{
                flex: 1,
                color: '#42A5F5',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#42A5F5',
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#42A5F5',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(66, 165, 245, 0.38)',
                },
              }}
            />
            <TextField
              value={Math.round(localStokWeight)}
              onChange={handleStokWeightInput}
              type="text"
              size="small"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  padding: '6px 0 6px 8px',
                  textAlign: 'right',
                },
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end" sx={{ ml: 0, mr: 1 }}>%</InputAdornment>,
              }}
              sx={{
                width: 80,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '6.67px',
                  height: 32,
                  paddingRight: 0,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#42A5F5',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '13.33px',
                  color: '#F2F2F3',
                },
                '& .MuiInputAdornment-root': {
                  color: 'rgba(242, 242, 243, 0.7)',
                },
              }}
            />
          </Stack>
        </Paper>

        {/* Stokastic Market Projections Slider */}
        <Paper
          sx={{
            backgroundColor: '#212223',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            p: 2,
          }}
        >
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: 1.5,
                color: '#F2F2F3',
              }}
            >
              Stokastic Market Projections
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 400,
                fontSize: '11.11px',
                lineHeight: 1.2,
                color: 'rgba(242, 242, 243, 0.7)',
              }}
            >
              Top-down true-odds modeling derived from optimized weighting of sharp sportsbook prices.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Slider
              value={localMarketWeight}
              onChange={handleMarketWeightChange}
              min={0}
              max={100}
              sx={{
                flex: 1,
                color: '#42A5F5',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#42A5F5',
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#42A5F5',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: 'rgba(66, 165, 245, 0.38)',
                },
              }}
            />
            <TextField
              value={Math.round(localMarketWeight)}
              onChange={handleMarketWeightInput}
              type="text"
              size="small"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: {
                  padding: '6px 0 6px 8px',
                  textAlign: 'right',
                },
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end" sx={{ ml: 0, mr: 1 }}>%</InputAdornment>,
              }}
              sx={{
                width: 80,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '6.67px',
                  height: 32,
                  paddingRight: 0,
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#42A5F5',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  fontFamily: '"Instrument Sans", sans-serif',
                  fontWeight: 400,
                  fontSize: '13.33px',
                  color: '#F2F2F3',
                },
                '& .MuiInputAdornment-root': {
                  color: 'rgba(242, 242, 243, 0.7)',
                },
              }}
            />
          </Stack>
        </Paper>

        {/* Apply Button */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              height: 46,
              borderRadius: '6.67px',
              backgroundColor: '#42A5F5',
              border: '1px solid rgba(42, 83, 195, 0.3)',
              fontFamily: '"Instrument Sans", sans-serif',
              fontWeight: 600,
              fontSize: '13.33px',
              lineHeight: 1.5,
              color: 'rgba(0, 0, 0, 0.87)',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </BottomSheetOrDialog>
  );
};

export default CustomizeBlendDialog;
